import { DEFAULT_SIZE_THRESHOLDS } from '@rundown/domain';
import type { Role, SizeThresholds, Workspace } from '@rundown/types';
import { useAuth } from '../auth/useAuth';

/**
 * The workspace the user is currently looking at, and their role in it.
 *
 * Rundown is multi-tenant but a user is normally in exactly one workspace, so the
 * current one is simply the first membership on the profile. There is no
 * workspace id in the URL yet; when a switcher arrives, this hook is the single
 * place that changes (read the slug from the route, match it against
 * `memberships`, fall back to the first) and every query key follows, because
 * they all take the id from here.
 */

export interface CurrentWorkspace {
  readonly workspace: Workspace;
  readonly role: Role;
}

/** Null while the profile is loading, or if the user belongs to no workspace. */
export function useWorkspace(): CurrentWorkspace | null {
  const { profile } = useAuth();

  // noUncheckedIndexedAccess: [0] is `ProfileMembership | undefined` on an empty
  // memberships array, which is a real state — an invited user not yet added.
  const membership = profile?.memberships[0];
  if (membership === undefined) return null;

  return { workspace: membership.workspace, role: membership.role };
}

/**
 * For screens that live behind <RequireAuth> and cannot render without a
 * workspace. Throws rather than returning null so the failure surfaces at the
 * error boundary instead of as a blank panel with no explanation.
 */
export function useRequiredWorkspace(): CurrentWorkspace {
  const current = useWorkspace();
  if (current === null) {
    throw new Error('No workspace on the profile: render this screen inside <RequireAuth>.');
  }
  return current;
}

/** Shorthand for the value that discriminates every query key in the app. */
export function useWorkspaceId(): string {
  return useRequiredWorkspace().workspace.id;
}

/**
 * What to call the delivery side of the product, in this workspace.
 *
 * Every screen that used to say a company name by hand now asks for it. The
 * fallback is generic rather than a company: a vendor name that has not loaded
 * yet should read as nothing in particular, not as somebody else's.
 */
export function useVendorName(): string {
  return useWorkspace()?.workspace.vendorName ?? 'the delivery team';
}

/**
 * Where this workspace draws the Small / Medium / Large / XL lines.
 *
 * Size is not stored and is no longer sent: every screen that shows a label
 * derives it from the request's hours, and this is the rule it derives it by.
 * One hook rather than a prop threaded through the card components, because the
 * screens that draw a size label (Home, Plan, Projects, Review, the request
 * pane, intake) have nothing else in common.
 *
 * The fallback is the product default and it only applies before the profile
 * has loaded — a label is cosmetic, so drawing it from the default for one
 * frame is better than a hook that throws inside a card.
 */
export function useSizeThresholds(): SizeThresholds {
  return useWorkspace()?.workspace.sizeThresholds ?? DEFAULT_SIZE_THRESHOLDS;
}
